import { Button, Paper, Typography } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { makeStyles } from "@/compat/muiStyles";
import { green } from "@mui/material/colors";

const useStyles = makeStyles((theme) => ({
    paper: { margin: "0 auto", maxWidth: 640, padding: theme.spacing(5, 4), textAlign: "center" },
    circle: {
        alignItems: "center",
        backgroundColor: theme.palette.mode === "dark" ? "rgba(118, 221, 120, 0.2)" : green[100],
        borderRadius: "50%",
        display: "flex",
        height: 64,
        justifyContent: "center",
        margin: "0 auto",
        width: 64,
    },
    icon: { color: green[600], fontSize: 40 },
    heading: { fontFamily: ["Sailec", "Arial", "sans-serif"].join(","), fontWeight: 700, marginTop: theme.spacing(2) },
    subtitle: { color: theme.palette.text.secondary, margin: `${theme.spacing(1)} auto ${theme.spacing(4)}`, maxWidth: 460 },
    paths: { display: "flex", flexDirection: "column", gap: theme.spacing(1.5), margin: "0 auto", maxWidth: 420 },
    note: { color: theme.palette.text.secondary, display: "block", marginTop: theme.spacing(3) },
}));

export interface NextStep {
    label: string;
    onClick: () => void;
    variant?: "contained" | "outlined" | "text";
}

interface RiskAssessmentNextStepsCardProps {
    heading: string;
    subtitle: string;
    steps: NextStep[];
    note?: string;
}

/**
 * "What next" confirmation, shared by the AI builder and the template flow so both
 * offer the same choices in the same shape. The AI one adds a third step, keeping
 * the template without creating an assessment from it yet.
 */
export default function RiskAssessmentNextStepsCard({ heading, subtitle, steps, note }: RiskAssessmentNextStepsCardProps) {
    const classes = useStyles();

    return (
        <Paper className={classes.paper}>
            <div className={classes.circle}>
                <CheckCircle className={classes.icon} />
            </div>
            <Typography variant="h6" className={classes.heading}>
                {heading}
            </Typography>
            <Typography variant="body2" className={classes.subtitle}>
                {subtitle}
            </Typography>

            <div className={classes.paths}>
                {steps.map((step, index) => (
                    <Button
                        key={index}
                        variant={step.variant ?? (index === 0 ? "contained" : "outlined")}
                        color="primary"
                        onClick={step.onClick}
                    >
                        {step.label}
                    </Button>
                ))}
            </div>

            {note && (
                <Typography variant="caption" className={classes.note}>
                    {note}
                </Typography>
            )}
        </Paper>
    );
}
